import { useEffect, useRef } from 'react'
import { useAtomValue } from 'jotai'
import { useLocation, useNavigate } from 'react-router'
import {
  authResolvedAtom,
  isAuthenticatedAtom,
  mfaRequiredAtom,
  newPasswordRequiredAtom
} from 'shared/auth/state/authAtoms'
import { authFreePaths } from 'shared/auth/authFreePaths'
import { useUserMe } from 'shared/api/userMeQuery'
import { getRedirectUrl } from 'shared/utils/url'

/**
 * テナントの正規ドメインへハードリダイレクトが必要なら実行し、行ったかを返す。
 * 別テナントのサブドメインに着地したユーザーを正しいドメインへ弾く。
 * (旧 AuthProvider で 3 箇所に重複していたブロックをここへ集約する。)
 */
const redirectToTenantDomain = (tenantName: string | undefined, domain: string | undefined): boolean => {
  const url = getRedirectUrl('/', tenantName, domain)
  const origin = url.startsWith('http') ? new URL(url).origin : window.location.origin
  if (window.location.origin !== origin) {
    window.location.href = url
    return true
  }
  return false
}

/**
 * 認証状態に応じた画面遷移。旧 AuthProvider の巨大な useEffect を切り出したもの。
 * Provider ではなく guard の責務として分離する。
 */
export const useAuthRedirect = (): void => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const resolved = useAtomValue(authResolvedAtom)
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const mfaRequired = useAtomValue(mfaRequiredAtom)
  const newPasswordRequired = useAtomValue(newPasswordRequiredAtom)
  const userMe = useUserMe()

  // テナントドメイン強制は初回判定直後に一度だけ(旧実装と同じく毎レンダーでは行わない)。
  const domainEnforcedRef = useRef(false)

  useEffect(() => {
    // 初回のセッション判定が終わるまでは遷移しない。
    if (!resolved) return

    const domain = process.env.JIZAIPAD_DOMAIN
    const tenantName = userMe?.tenants?.[0]?.nameAlphabet
    const onFreePath = authFreePaths.includes(pathname)

    // 認証済みかつ初回のみ: 誤テナントドメインなら正規ドメインへ弾く。
    if (isAuthenticated && !domainEnforcedRef.current) {
      domainEnforcedRef.current = true
      if (domain !== 'localhost' && !onFreePath && redirectToTenantDomain(tenantName, domain)) {
        return
      }
    }

    // 未認証で保護ページに来たら /login へ。
    if (!isAuthenticated && !mfaRequired && !newPasswordRequired && !onFreePath) {
      navigate('/login', { replace: true })
      return
    }

    // 認証済みで /login に居るならトップ(または正規ドメイン)へ。
    if (isAuthenticated && !mfaRequired && !newPasswordRequired && pathname === '/login') {
      if (domain !== 'localhost') {
        if (!redirectToTenantDomain(tenantName, domain)) {
          navigate('/', { replace: true })
        }
      } else {
        navigate('/', { replace: true })
      }
    }
  }, [resolved, isAuthenticated, mfaRequired, newPasswordRequired, pathname, userMe?.tenants, navigate])
}
